import { Component, effect, inject, input, output } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { AppleAirPlaySecurityPreset } from '@rocksoft/mdm-sdk-v2';

@Component({
  selector: 'ml-acp-airplay-ui',
  templateUrl: './airplay.component.html',
  styleUrls: ['./airplay.component.scss'],
  imports: [ReactiveFormsModule, FaIconComponent],
})
export class AirplayComponent {
  private formBuilder = inject(UntypedFormBuilder);

  readonly airplayPreset = input.required<AppleAirPlaySecurityPreset>();
  readonly airplayPresetChange = output<AppleAirPlaySecurityPreset>();

  readonly editingInput = input(false);
  readonly allowToggle = input(true);

  readonly airplayPresetForm: UntypedFormGroup;

  editing = false;
  showPassword = false;

  constructor() {
    this.airplayPresetForm = this.createForm();

    effect(() => {
      this.editing = this.editingInput();
    });

    effect(() => {
      const airplayPreset = this.airplayPreset();
      this.rebuildForm(airplayPreset);
    });
  }

  doneEditing(): boolean {
    if (!this.editing) {
      return true;
    }

    if (this.airplayPresetForm.status !== 'VALID') {
      return false;
    }

    const result = this.populate();
    this.airplayPresetChange.emit(result);
    return true;
  }

  toggleEditing() {
    // Toggle if we are not editing, or if the edited form validates
    if (!this.editing || this.doneEditing()) {
      this.editing = !this.editing;
    }
  }

  private createForm(): UntypedFormGroup {
    return this.formBuilder.group({
      securityType: ['', Validators.required],
      accessType: ['', Validators.required],
      password: '',
    });
  }

  private rebuildForm(airplayPreset: AppleAirPlaySecurityPreset) {
    this.airplayPresetForm.reset({
      securityType: airplayPreset.securityType,
      accessType: airplayPreset.accessType,
      password: airplayPreset.password,
    });
  }

  private populate(): AppleAirPlaySecurityPreset {
    const airplayPreset = this.airplayPreset();

    const airplayProfile: AppleAirPlaySecurityPreset = {
      uniqueId: airplayPreset.uniqueId,
      id: airplayPreset.id,
      securityType: this.airplayPresetForm.get('securityType')?.value,
      accessType: this.airplayPresetForm.get('accessType')?.value,
      password: this.airplayPresetForm.get('password')?.value,
      payloadIdentifier: airplayPreset.payloadIdentifier,
      payloadOrganization: airplayPreset.payloadOrganization,
      payloadType: airplayPreset.payloadType,
      payloadUUID: airplayPreset.payloadUUID,
      payloadVersion: airplayPreset.payloadVersion,
      payloadDescription: airplayPreset.payloadDescription,
      payloadDisplayName: airplayPreset.payloadDisplayName,
    };

    return airplayProfile;
  }
}
